import React, {MouseEvent, ReactNode, useCallback, useRef, useState} from 'react';
import cx from 'classnames';
import {
    Icon,
    PopoverPlacement,
    PriorityIcon,
    Separator,
    Title
} from '@priority-software/priority-style-react';
import {helpPopoverRef} from '../../../_shared/Help/constants';
import {ModernTooltip} from '../../Common/Tooltip/ModernTooltip';
import {texts} from '../../../features/Automations/texts';
import styles from './styles.module.scss';
import {sendMonitor} from '../../../utils/monitoring';
import {createDataTestId} from '../../../utils/dataTestId';

type Props = {
    mainTitle: string;
    subTitle?: ReactNode;
    description?: string;
    helpText?: string;
    showSeparator?: boolean;
    tooltipPortalID?: string;
    monitorTransactionText?: string;
    prefixIcon?: ReactNode;
    subTitleColor?: 'primary-500' | 'secondary-500';
    descriptionColor?: 'primary-500' | 'secondary-400';
};

export const DialogToolbarContent = (props: Props) => {
    const {
        mainTitle,
        subTitle,
        description,
        helpText,
        showSeparator = true,
        tooltipPortalID,
        monitorTransactionText,
        prefixIcon,
        subTitleColor,
        descriptionColor
    } = props;
    const wrapRef = useRef(null);
    const [tooltipEvents, setTooltipEvents] = useState(true);

    const showHelp = useCallback(
        (e: MouseEvent<HTMLElement>) => {
            setTooltipEvents(false);
            if (!!monitorTransactionText) {
                sendMonitor(monitorTransactionText);
            }

            helpPopoverRef.current.showHelpPopover({
                title: texts.help_popover_title,
                content: helpText,
                anchor: e.currentTarget,
                portalSelector: wrapRef.current,
                onClose: () => {
                    setTooltipEvents(true);
                },
                placement: PopoverPlacement.leftStart
            });
        },
        [helpText, setTooltipEvents]
    );

    return (
        <div
            className={styles.toolbarTop}
            ref={wrapRef}
            {...createDataTestId('toolbar-section', false)}
        >
            <div className={styles.toolbarTopLeftPart}>
                {prefixIcon}
                <Title
                    skin="h4"
                    textColor="primary-700"
                    text={mainTitle}
                    {...createDataTestId('common-title', true)}
                />

                {subTitle && (
                    <Title
                        skin="h4"
                        textColor={subTitleColor || 'secondary-500'}
                        componentClasses={styles.subTitle}
                        {...createDataTestId('common-title', true)}
                    >
                        {subTitle}
                    </Title>
                )}

                {description && (
                    <Title
                        skin="h4"
                        textColor={descriptionColor || 'secondary-400'}
                        componentClasses={styles.subTitle}
                        {...createDataTestId('common-title', true)}
                    >
                        {description}
                    </Title>
                )}
            </div>

            <div className={styles.toolbarTopRightPart}>
                {!!helpText && (
                    <ModernTooltip
                        tooltip={texts.help_popover_title}
                        maxWidth={400}
                        arrowOffset={8}
                        position="left-start"
                        tooltipOffset={8}
                        portalId={tooltipPortalID}
                        wrapperClassname={cx({
                            [styles.pointerNone]: !tooltipEvents
                        })}
                    >
                        <Icon
                            onClick={showHelp}
                            icon={PriorityIcon.questionCircle}
                            iconColor="secondary-500"
                        />
                    </ModernTooltip>
                )}

                {showSeparator && !!helpText && (
                    <Separator componentClasses={styles.separator} height={30} />
                )}
            </div>
        </div>
    );
};
